import React, { useRef, useEffect } from 'react';
import { init, destroy, setEnabled, setColor } from '../../effects/fluid';

/**
 * FluidCursorLayer — fullscreen WebGL canvas for the smokey fluid cursor effect.
 *
 * Props:
 *   enabled (boolean) — whether the effect is active (false = paused + hidden)
 *   color   (string)  — hex color for the smoke, e.g. "#6d28ff". Default: dark violet.
 *   opacity (number)  — canvas CSS opacity (0–1). Default: 0.1.
 *
 * Layering: positioned fixed, above background video but below interactive UI.
 * pointer-events: none — never blocks clicks.
 * Desktop-only check and prefers-reduced-motion are handled by the parent via `enabled`.
 *
 * ALWAYS mounted (never conditionally rendered) — conditional rendering would cause
 * React to unmount/remount on state changes, triggering background video restarts.
 */

/** Parse a hex color string to normalized { r, g, b } floats (0–1). */
function hexToNormalizedRGB(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  // Scale down for subtlety — the fluid engine adds colors additively,
  // so raw 255-scaled values would be far too bright.
  return { r: r * 0.18, g: g * 0.18, b: b * 0.18 };
}

export function FluidCursorLayer({ enabled, color, opacity }) {
  const canvasRef = useRef(null);
  const handleRef = useRef(null);
  const prevColorRef = useRef(null);

  // Init WebGL on mount, destroy on unmount — happens exactly ONCE.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handle = init(canvas);
    handleRef.current = handle;

    return () => {
      destroy(handle);
      handleRef.current = null;
    };
  }, []);

  // Sync enabled state (RAF start/stop + canvas opacity is handled separately)
  useEffect(() => {
    setEnabled(handleRef.current, enabled);
  }, [enabled]);

  // Sync color — only call setColor when the hex value actually changes
  useEffect(() => {
    if (color === prevColorRef.current) return;
    prevColorRef.current = color;
    const rgb = hexToNormalizedRGB(color);
    if (rgb && handleRef.current) {
      setColor(handleRef.current, rgb.r, rgb.g, rgb.b);
    }
  }, [color]);

  // Resolve opacity: 0 when disabled, user-defined value when enabled
  const resolvedOpacity = enabled ? (typeof opacity === 'number' ? opacity : 0.1) : 0;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fluid-cursor-canvas"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 8,
        pointerEvents: 'none',
        opacity: resolvedOpacity,
        transition: 'opacity 0.5s ease',
      }}
    />
  );
}
